import React, {ButtonHTMLAttributes} from 'react';

export default function LatchButton(props: React.PropsWithChildren<{ controller: LatchController }> & React.HTMLProps<HTMLButtonElement>) {
	const group = React.useContext(LatchGroup);
	const btnProps = React.useMemo(() => {
		const btn = Object.assign({}, props) as { [T in keyof typeof props]: T extends 'controller' | 'children' ? never : typeof props[T] };

		delete btn.controller;
		delete btn.children;

		return  btn;
	}, [props]);

	return <button {...props as ButtonHTMLAttributes<HTMLButtonElement>} onClick={() => props.controller.dispatch('click')}>
		{props.children}
	</button>
}

export const LatchGroup = React.createContext<symbol>(Symbol());

export class LatchController {
	#target = new EventTarget();
	#controllers: AbortController[] = [];

	constructor() {

	}

	dispatch<T>(event: string, payload?: T) {
		this.#target.dispatchEvent(new LatchEvent(event, payload));
	}

	private on<T>(event: string, handler: (payload: T) => void, once: boolean = false) {
		const controller = new AbortController();

		this.#controllers.push(controller);

		this.#target.addEventListener(event, e => {
			if (e instanceof LatchEvent)
				handler(e.payload)
		},  { signal: controller.signal, once });
	}

	private cancelAll() {
		for (const controller of this.#controllers.splice(0, this.#controllers.length))
			controller.abort();
	}

	bind(latchButton: LatchButton) {
		this.on('click', () => latchButton.onLatch());
		this.on('unlatch', () => latchButton.onEnd());
		this.on('cancel', () => latchButton.onCancel());
	}

	unlatch() {
		this.dispatch('unlatch');
	}
}

class LatchEvent<Payload> extends Event {
	constructor(name: string, readonly payload: Payload) {
		super(name);
	}
}

export interface LatchButton {
	onLatch(): Promise<void> | void;
	onCancel(): Promise<void> | void;
	onEnd(): Promise<void> | void;
}